import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Accordion } from 'semantic-ui-react';
import fetch, { fetchConfig } from '@/hooks/fetch';
import Filter from '@/components/Filter';
import NoRows from '@/components/table/NoRows';
import RolePanelContainer, { mapDispatchToProps, mapStateToProps } from '../components/RolePanelContainer';
import RoleName from './RoleName';
import RoleDescription from './RoleDescription';
import PermissionsAccordion from './PermissionsAccordion';
import {
  permissionCategories,
  tokenPermissionCategories,
  adminPermissions,
  datasetPermissions,
  requestPermissions,
  collectionPermissions,
  datasetTokenPermissions,
  requestTokenPermissions,
  adminTokenPermissions,
  roleGroupings,
  allPermissionCategories,
} from '../constants';
import RoleSmartPermissions from './RoleSmartPermissions';
import RoleTokens from './RoleTokens';
import { filterPermissions } from '../helper';
import RoleActionsList from '../roleactions/containers/RoleActionsList';
import RoleErrorMessages from '../components/RoleErrorMessages';

const RolePanelNarrow = ({
  canRead,
  category,
  code,
  description,
  exists,
  fetchData,
  filter,
  grouping,
  isDirty,
  name,
  onChange,
  panels,
  readErrors,
  readStatus,
  role,
  updateErrors,
  updateFilter,
  updatePanels,
  updateProperties,
  url,
}) => {
  const intl = useIntl();
  fetch.useFetchByCode(fetchData, category, code, readStatus, fetchConfig([], () => !role));
  const filteredCategories = filter
    ? filterPermissions(allPermissionCategories, filter, grouping, intl) : allPermissionCategories;

  const userPanels = [
    { title: 'permissions.datasets.title', categories: datasetPermissions },
    { title: 'permissions.requests.title', categories: requestPermissions },
    { title: 'permissions.admin.title', categories: adminPermissions },
    { title: 'permissions.collections.title', categories: collectionPermissions },
    { title: 'permissions.search.title', categories: [permissionCategories.SEARCH] },
    { title: 'permissions.cohort.title', categories: [permissionCategories.COHORTS] },
    { title: 'permissions.usergroups.title', categories: [permissionCategories.USER_GROUPS] },
  ];
  const tokenPanels = [
    { title: 'permissions.datasets.title', categories: datasetTokenPermissions },
    { title: 'permissions.requests.title', categories: requestTokenPermissions },
    { title: 'permissions.admin.title', categories: adminTokenPermissions },
    { title: 'permissions.collections.title', categories: collectionPermissions },
    { title: 'permissions.search.title', categories: [tokenPermissionCategories.SEARCH] },
    { title: 'permissions.cohort.title', categories: [tokenPermissionCategories.COHORTS] },
  ];

  return (
    <RolePanelContainer
      canRead={canRead}
      description={description}
      exists={exists}
      isDirty={isDirty}
      name={name}
      onChange={onChange}
      readErrors={readErrors}
      readStatus={readStatus}
      url={url}
    >
      <div className="aridhia-role-panel-narrow overflow-x-hidden">
        <RoleErrorMessages
          category={category}
          code={code}
          updateErrors={updateErrors}
          updateProperties={updateProperties}
        />
        <RoleName code={code} category={category} />
        <RoleDescription code={code} category={category} />
        {grouping === roleGroupings.USERS && (
        <>
          <RoleTokens code={code} category={category} />
          <RoleActionsList code={code} category={category} />
        </>
        )}
        <Filter
          fluid
          className="aridhia-list-filter"
          filterTerm={filter}
          updateFilter={({ value }) => updateFilter(code, category, value)}
          label={<RoleSmartPermissions code={code} category={category} />}
        />
        {filteredCategories.length === 0 && (
          <NoRows icon="lock" entity="permission" />
        )}
        {grouping === roleGroupings.USERS && filteredCategories.length > 0 && (
        <Accordion>
          {userPanels.map((panel, index) => (
            <PermissionsAccordion
              key={`users_permissions_accordion_${panel.title}`}
              title={panel.title}
              active={panels.single.includes(index)}
              index={index}
              onClick={() => updatePanels(code, category, index, 'single')}
              code={code}
              category={category}
              categories={panel.categories}
              grouping={grouping}
            />
          ))}
        </Accordion>
        )}
        {grouping === roleGroupings.TOKENS && filteredCategories.length > 0 && (
        <Accordion>
          {tokenPanels.map((panel, index) => (
            <PermissionsAccordion
              key={`tokens_permissions_accordion_${panel.title}`}
              title={panel.title}
              active={panels.single.includes(index)}
              index={index}
              onClick={() => updatePanels(code, category, index, 'single')}
              code={code}
              category={category}
              categories={panel.categories}
              grouping={grouping}
            />
          ))}
        </Accordion>
        )}
      </div>
    </RolePanelContainer>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(RolePanelNarrow);
